import router from '@/router';
import store from '@/store';
import Analytics from '@/mixins/Analytics';
import {
  ApolloLink,
  HttpLink,
  split,
  from,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { decodeJwt } from '@/utilities';
import { isLocked } from '@/utilities/JwtRefreshTokensLock';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';

// Headers to be passed to HTTP and WS requests
let startingHeaders = {};

const setStartingHeaders = (additionalHeaders = {}) => {
  // Pass json-naming-strategy header as camel or snake to change response keys (default is camel)

  startingHeaders = {
    ...additionalHeaders,
    'acv-client-version': import.meta.env.VITE_VERSION,
    'acv-client-type': 'desktop',
    'acv-client-os': 'web',
    'acv-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'Content-Type': 'application/json',
  };
};

const getHeaders = async () => {
  const { jwtToken } = await store.dispatch('getJwtTokens');

  return {
    ...startingHeaders,
    'acv-user-id': store.getters.currentUser.id,
    Authorization: `Bearer ${jwtToken}`,
  };
};

// Only process query while not idle
const idleLink = () => new ApolloLink((operation, forward) => {
  const { isIdle } = store.getters;
  if (!isIdle) {
    return forward(operation);
  }

  return null;
});

// Refresh token if close to expiration (should match axios interceptors)
const refreshTokenLink = () => new ApolloLink(async (operation, forward) => {
  const {
    lastUpdated,
    jwtToken,
    refreshToken,
    lastRefreshFailed = false,
  } = await store.dispatch('getJwtTokens');

  const decodedJwtToken = decodeJwt(jwtToken) ?? '';
  const jwtExpirationVal = new Date(0);
  jwtExpirationVal.setUTCSeconds(decodedJwtToken?.exp ?? 0);

  const canRefreshTokens = !!(jwtToken && refreshToken && lastUpdated);

  if (canRefreshTokens && !lastRefreshFailed) {
    const refreshIsLocked = isLocked();
    const currentDate = new Date();

    const jwtDifferenceInMinutes = getDifferenceInMinutes(
      jwtExpirationVal, // start date
      currentDate, // end date
    );

    const luDifferenceInMinutes = getDifferenceInMinutes(
      currentDate,
      lastUpdated,
    );

    const atLeast12MinsSinceLastUpdate = luDifferenceInMinutes >= 12;

    const tokensCloseToExpiration = jwtDifferenceInMinutes < 3 || atLeast12MinsSinceLastUpdate;
    if (!refreshIsLocked && tokensCloseToExpiration) {
      await store.dispatch('refreshJwtTokens');
    } else if (refreshIsLocked && tokensCloseToExpiration) {
      await store.dispatch('waitForOtherWindowToRefreshJwtToken');
    }
  }

  return forward(operation);
});

// Set headers for authentication
const authenticationLink = () => new ApolloLink(async (operation, forward) => {
  const headersToUse = await getHeaders();
  operation.setContext(({ headers }) => ({
    headers: {
      ...headersToUse,
      ...headers,
    },
  }));

  return forward(operation);
});

// Transform the response properties if necessary
const afterResponseLink = () => new ApolloLink((operation, forward) => forward(operation));

// Log network or graphql errors, redirect on 401 - Terminal Link
const onErrorLink = () => onError(({ graphQLErrors, networkError }) => {
  if (networkError?.statusCode === 401) {
    Analytics.methods.noticeError('GraphQL nauthorized network error', { networkError });
    router.push('/logout');
  }

  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      Analytics.methods.noticeError('GraphQL definition error', { error });
    });
  }
});

// Connection to Websocket Setup for subscriptions - Terminal Link
const setupSubscriptionLink = (wsUri = '') => new GraphQLWsLink(
  createClient({
    url: wsUri,
    reconnect: true,
    connectionParams: async () => {
      const headersToUse = await getHeaders();

      return {
        ...headersToUse,
      };
    },
  }),
);

// Connection to HTTP Query for queries and mutations - Terminal Link
const setupHttpLink = (httpUri = '') => new HttpLink({ uri: httpUri });

// Create link combination for ApolloClient
const getCombinedLink = (baseDomain = '', additionalHeaders = {}) => {
  if (!baseDomain) {
    throw new Error('A base domain is required');
  }

  setStartingHeaders(additionalHeaders);

  const domainLinks = {
    http: `https://${baseDomain}`,
    // ws: `wss://${baseDomain}`,
    ws: 'wss://gaia.internal.latest.acvauctions.com/query', // temporary
  };

  // additively include the links to the chain (order matters)
  const combinedLink = from([
    idleLink(),
    refreshTokenLink(),
    authenticationLink(),
    afterResponseLink(),
    onErrorLink(),
  ]);

  // Splits the requests to point to websockets or http by query with the combinedLink
  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition'
        && definition.operation === 'subscription'
      );
    },
    combinedLink.concat(setupSubscriptionLink(domainLinks.ws)),
    combinedLink.concat(setupHttpLink(domainLinks.http)),
  );

  return link;
};

export default getCombinedLink;
